import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageCircle, Send, User, Clock, CheckCircle2, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface SupportMessage {
  id: string;
  chat_id: string;
  sender_type: string;
  sender_name: string | null;
  sender_email: string | null;
  message: string;
  created_at: string;
  is_read: boolean;
}

interface SupportChat {
  id: string;
  customer_email: string;
  customer_name: string | null;
  status: string;
  priority: string;
  subject: string | null;
  created_at: string;
  updated_at: string;
  last_message_at: string;
  assigned_admin_id: string | null;
  is_closed: boolean;
}

export const LiveSupportManagement = () => {
  const [chats, setChats] = useState<SupportChat[]>([]);
  const [selectedChat, setSelectedChat] = useState<SupportChat | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("new");
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadChats();
  }, []);

  useEffect(() => {
    if (selectedChat) {
      loadMessages(selectedChat.id);
    }
  }, [selectedChat]);

  // Scroll to bottom when messages change
  useEffect(() => {
    const scrollToBottom = () => {
      if (scrollAreaRef.current) {
        const scrollElement = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
        if (scrollElement) {
          scrollElement.scrollTop = scrollElement.scrollHeight;
        }
      }
    };
    
    // Use setTimeout to ensure DOM has updated
    setTimeout(scrollToBottom, 100);
  }, [messages]);

  // Real-time subscription for new chats and messages
  useEffect(() => {
    console.log('=== SETTING UP REALTIME SUBSCRIPTIONS ===');
    
    const chatsChannel = supabase
      .channel('support_chats_admin')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'support_chats'
        },
        (payload) => {
          console.log('Chat realtime event:', payload);
          loadChats();
        }
      )
      .subscribe((status) => {
        console.log('Chats channel status:', status);
      });

    const messagesChannel = supabase
      .channel('support_messages_admin')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'support_messages'
        },
        (payload) => {
          console.log('Message realtime event:', payload);
          const newMessage = payload.new as SupportMessage;
          
          // Update messages if this is for the selected chat
          if (selectedChat && newMessage.chat_id === selectedChat.id) {
            console.log('Adding message to current chat');
            setMessages(prev => [...prev, newMessage]);
          }
          
          // Show notification for new customer messages
          if (newMessage.sender_type === 'customer') {
            console.log('Showing notification for customer message');
            toast({
              title: "New customer message",
              description: `From: ${newMessage.sender_name || newMessage.sender_email}`,
            });
            
            // Reopen closed chat when customer sends a message
            reopenClosedChat(newMessage.chat_id);
          }
          
          // Refresh chats list to update last message time
          console.log('Refreshing chats list');
          loadChats();
        }
      )
      .subscribe((status) => {
        console.log('Messages channel status:', status);
      });

    return () => {
      console.log('=== CLEANING UP REALTIME SUBSCRIPTIONS ===');
      supabase.removeChannel(chatsChannel);
      supabase.removeChannel(messagesChannel);
    };
  }, [selectedChat, toast]);

  const loadChats = async () => {
    try {
      const { data } = await supabase
        .from('support_chats')
        .select('*')
        .order('last_message_at', { ascending: false });

      if (data) {
        setChats(data);
      }
    } catch (error) {
      console.error('Error loading chats:', error);
    }
  };

  const loadMessages = async (chatId: string) => {
    try {
      const { data } = await supabase
        .from('support_messages')
        .select('*')
        .eq('chat_id', chatId)
        .order('created_at', { ascending: true });

      if (data) {
        setMessages(data);
        
        // Mark messages as read
        await supabase
          .from('support_messages')
          .update({ is_read: true })
          .eq('chat_id', chatId)
          .eq('sender_type', 'customer')
          .eq('is_read', false);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedChat) return;

    console.log('=== ADMIN MESSAGE SEND DEBUG ===');
    console.log('Selected chat:', selectedChat);
    console.log('Message to send:', newMessage.trim());
    
    setIsLoading(true);
    
    try {
      // Check authentication first
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      console.log('Current user:', { id: user?.id, email: user?.email });
      
      if (userError || !user) {
        throw new Error('Authentication failed - please refresh and try again');
      }

      // Prepare message data
      const messageData = {
        chat_id: selectedChat.id,
        sender_type: 'admin',
        sender_name: 'Support Team',
        message: newMessage.trim()
      };
      
      console.log('Attempting to insert:', messageData);

      // Try the insert
      const { data, error } = await supabase
        .from('support_messages')
        .insert(messageData)
        .select();

      console.log('Insert response:', { data, error });

      if (error) {
        console.error('Supabase error:', error);
        throw new Error(`Database error: ${error.message}`);
      }

      if (!data || data.length === 0) {
        throw new Error('Message was not saved - no data returned');
      }

      console.log('SUCCESS: Message sent successfully!', data[0]);
      setNewMessage("");
      
      toast({
        title: "Message sent successfully",
        description: "Your reply has been delivered to the customer.",
      });
      
    } catch (error) {
      console.error('=== SEND MESSAGE ERROR ===');
      console.error('Error type:', typeof error);
      console.error('Error message:', error instanceof Error ? error.message : String(error));
      console.error('Full error object:', error);
      
      toast({
        title: "Failed to send message", 
        description: error instanceof Error ? error.message : 'Unknown error occurred',
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
      console.log('=== END DEBUG ===');
    }
  };

  const updateChatStatus = async (chatId: string, status: string) => {
    try {
      const { error } = await supabase
        .from('support_chats')
        .update({ status })
        .eq('id', chatId);

      if (error) throw error;

      // Update local state
      setChats(prev => prev.map(chat => 
        chat.id === chatId ? { ...chat, status } : chat
      ));
      
      if (selectedChat && selectedChat.id === chatId) {
        setSelectedChat(prev => prev ? { ...prev, status } : null);
      }

      toast({
        title: "Status updated",
        description: `Chat status changed to ${status}`,
      });
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: "Error updating status",
        description: "Please try again.",
        variant: "destructive"
      });
    }
  };

  const closeChat = async (chatId: string) => {
    try {
      const { error } = await supabase
        .from('support_chats')
        .update({ is_closed: true, status: 'closed' })
        .eq('id', chatId);

      if (error) throw error;

      loadChats();
      if (selectedChat && selectedChat.id === chatId) {
        setSelectedChat(null);
      }

      toast({
        title: "Chat closed",
        description: "The chat has been closed successfully.",
      });
    } catch (error) {
      console.error('Error closing chat:', error);
      toast({
        title: "Error closing chat",
        description: "Please try again.",
        variant: "destructive"
      });
    }
  };

  const reopenClosedChat = async (chatId: string) => {
    try {
      const { error } = await supabase
        .from('support_chats')
        .update({ is_closed: false, status: 'in_progress' })
        .eq('id', chatId)
        .eq('is_closed', true);

      if (error) throw error;

      // Switch to Active tab when reopening a chat
      setActiveTab('in_progress');
      
      console.log('Chat reopened and switched to Active tab');
    } catch (error) {
      console.error('Error reopening chat:', error);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'new':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'in_progress':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'replied':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      default:
        return <MessageCircle className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
        return 'destructive';
      case 'in_progress':
        return 'secondary';
      case 'replied':
        return 'default';
      default:
        return 'outline';
    }
  };

  const filterChatsByStatus = (status: string) => {
    switch (status) {
      case 'new':
        return chats.filter(chat => chat.status === 'new' && !chat.is_closed);
      case 'in_progress':
        return chats.filter(chat => chat.status === 'in_progress' && !chat.is_closed);
      case 'replied':
        return chats.filter(chat => chat.status === 'replied' && !chat.is_closed);
      case 'closed':
        return chats.filter(chat => chat.is_closed);
      case 'all':
        return chats.filter(chat => !chat.is_closed);
      default:
        return chats;
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="h-full flex">
      {/* Chat List */}
      <div className="w-1/3 border-r">
        <div className="p-4 border-b">
          <h3 className="font-semibold mb-3">Support Chats</h3>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full h-auto flex flex-wrap gap-1 bg-muted p-1">
              <TabsTrigger value="new" className="flex-1 min-w-[60px]">New</TabsTrigger>
              <TabsTrigger value="in_progress" className="flex-1 min-w-[60px]">Active</TabsTrigger>
              <TabsTrigger value="replied" className="flex-1 min-w-[60px]">Replied</TabsTrigger>
              <TabsTrigger value="closed" className="flex-1 min-w-[60px]">Closed</TabsTrigger>
              <TabsTrigger value="all" className="flex-1 min-w-[60px]">All</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <ScrollArea className="h-[calc(100vh-200px)]">
          <div className="p-2">
            {filterChatsByStatus(activeTab).map((chat) => (
              <Card
                key={chat.id}
                className={`mb-2 cursor-pointer transition-colors hover:bg-muted/50 ${
                  selectedChat?.id === chat.id ? 'border-primary' : ''
                }`}
                onClick={() => setSelectedChat(chat)}
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      <span className="font-medium text-sm">
                        {chat.customer_name || chat.customer_email}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      {getStatusIcon(chat.status)}
                      <Badge variant={getStatusColor(chat.status) as any} className="text-xs">
                        {chat.status}
                      </Badge>
                    </div>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-1">
                    {chat.subject || 'No subject'}
                  </p>
                  
                  <p className="text-xs text-muted-foreground">
                    {new Date(chat.last_message_at).toLocaleString()}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* Chat Messages */}
      <div className="flex-1 flex flex-col">
        {selectedChat ? (
          <>
            <div className="p-4 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">
                    {selectedChat.customer_name || selectedChat.customer_email}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {selectedChat.subject || 'No subject'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Select
                    value={selectedChat.status}
                    onValueChange={(value) => updateChatStatus(selectedChat.id, value)}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="replied">Replied</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => closeChat(selectedChat.id)}
                  >
                    Close Chat
                  </Button>
                </div>
              </div>
            </div>

            <ScrollArea className="flex-1 p-4" ref={scrollAreaRef}>
              <div className="space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] p-3 rounded-lg ${
                        message.sender_type === 'admin'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted'
                      }`}
                    >
                      <div className="font-medium text-sm mb-1">
                        {message.sender_type === 'admin' ? 'Support Team' : message.sender_name || 'Customer'}
                      </div>
                      <div className="whitespace-pre-wrap">{message.message}</div>
                      <div className="text-xs opacity-70 mt-2">
                        {new Date(message.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <div className="p-4 border-t">
              <div className="flex gap-2">
                <Input
                  placeholder="Type your reply..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={isLoading}
                />
                <Button
                  onClick={sendMessage}
                  disabled={isLoading || !newMessage.trim()}
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">Select a chat to start</h3>
              <p className="text-muted-foreground">
                Choose a chat from the list to view and respond to messages
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};